import { useState } from "react";
import { cn } from "@/lib/utils";
import type { EvidenceEntry } from "@/types/deal";
import { FindingEvidenceDrawer } from "@/components/qoe/FindingEvidenceDrawer";
import {
  CATALOGUE_SIZE,
  RISK_SECTIONS,
  SECTION_COUNT,
  TIER_FINDING_COUNTS,
} from "@/lib/qoeCatalogue.generated";

// ── Types ──────────────────────────────────────────────────────────────────────

export type FindingScore = "pass" | "review" | "flag" | "pending";
export type EngagementTier = "essentials" | "comprehensive";

/**
 * FINDINGS-DOMAIN-1: why a finding has no score. Sent by
 * GET /qoe/deals/{id}/findings; optional so older callers still typecheck.
 * "pending" alone could not tell "nobody bought this section" apart from
 * "we are waiting on the seller" — the report already distinguishes them,
 * and this is what makes the screen say the same thing.
 */
export type FindingLifecycleState =
  | "assessed" | "not_in_scope" | "data_missing" | "unscored";

export interface Finding {
  number: number;
  title: string;
  body?: string;
  section: string;
  score: FindingScore;
  score_note?: string;
  is_essentials: boolean;
  pending?: boolean;
  evidence?: EvidenceEntry[];
  state?: FindingLifecycleState;
  in_scope?: boolean;
  required_inputs?: string;
}

/** A finding is out of scope only when the API says so — never inferred. */
function isOutOfScope(f: Finding): boolean {
  return f.state === "not_in_scope" || f.in_scope === false;
}

// ── Section registry ───────────────────────────────────────────────────────────

// Generated from core/qoe/findings/risk_sections.py — see
// scripts/gen_frontend_catalogue.py. Hand-maintaining this list is what let the
// screen and the report disagree about which sections exist.
const SECTIONS = RISK_SECTIONS;

// ── Grade computation ──────────────────────────────────────────────────────────

export function computeQoEGrade(findings: Finding[]): string {
  const flags   = findings.filter(f => f.score === "flag").length;
  const reviews = findings.filter(f => f.score === "review").length;
  if (flags === 0 && reviews <= 3)  return "A";
  if (flags <= 2 || reviews <= 8)   return "B";
  if (flags <= 5)                   return "C";
  if (flags <= 9)                   return "D";
  return "F";
}

// ── Score badge ────────────────────────────────────────────────────────────────

function ScoreBadge({ score }: { score: FindingScore }) {
  const styles: Record<FindingScore, string> = {
    pass:    "bg-green-50  text-green-800  border-green-200",
    review:  "bg-amber-50  text-amber-800  border-amber-200",
    flag:    "bg-red-50    text-red-800    border-red-200",
    pending: "bg-gray-50   text-gray-500   border-gray-200",
  };
  const labels: Record<FindingScore, string> = {
    pass: "PASS", review: "REVIEW", flag: "FLAG", pending: "PENDING",
  };
  return (
    <span className={cn(
      "inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold tracking-wide",
      styles[score],
    )}>
      {labels[score]}
    </span>
  );
}

/** Badge for an unscored finding, naming the reason the report gives. */
function StateBadge({ state }: { state: FindingLifecycleState }) {
  const styles: Record<string, string> = {
    not_in_scope: "bg-gray-50   text-gray-400 border-gray-200",
    data_missing: "bg-amber-50  text-amber-700 border-amber-200",
    unscored:     "bg-gray-50   text-gray-500 border-gray-200",
  };
  const labels: Record<string, string> = {
    not_in_scope: "NOT IN SCOPE",
    data_missing: "AWAITING DATA",
    unscored:     "PENDING REVIEW",
  };
  return (
    <span className={cn(
      "inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold tracking-wide",
      styles[state] ?? styles.unscored,
    )}>
      {labels[state] ?? labels.unscored}
    </span>
  );
}

// ── Grade badge ────────────────────────────────────────────────────────────────

function GradeBadge({ grade }: { grade: string }) {
  const styles: Record<string, string> = {
    A: "bg-green-100 text-green-900 border-green-300",
    B: "bg-blue-100  text-blue-900  border-blue-300",
    C: "bg-amber-100 text-amber-900 border-amber-300",
    D: "bg-orange-100 text-orange-900 border-orange-300",
    F: "bg-red-100   text-red-900   border-red-300",
  };
  return (
    <span className={cn(
      "inline-flex h-10 w-10 items-center justify-center rounded-full border-2 text-lg font-bold",
      styles[grade] ?? "bg-gray-100 text-gray-900",
    )}>
      {grade}
    </span>
  );
}

// ── Section rail item ──────────────────────────────────────────────────────────

function SectionRailItem({
  section,
  findings,
  active,
  onClick,
}: {
  section: { key: string; title: string; count: number };
  findings: Finding[];
  active: boolean;
  onClick: () => void;
}) {
  const sectionFindings = findings.filter(f => f.section === section.key);
  // Progress is against what this engagement actually bought. Using the full
  // catalogue count caps a lite deal's bar at a fraction it can never fill.
  const inScope = sectionFindings.filter(f => !isOutOfScope(f));
  const denominator = inScope.length || section.count;
  const scored = inScope.filter(f => f.score !== "pending").length;
  const pct = denominator > 0 ? (scored / denominator) * 100 : 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
        active
          ? "bg-[--color-primary] text-white"
          : "hover:bg-gray-100 text-gray-700",
      )}
    >
      <span className={cn(
        "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold border",
        active ? "border-white/40 bg-white/20 text-white" : "border-gray-300 bg-white text-gray-600",
      )}>
        {section.key}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{section.title}</p>
        <div className="mt-1 h-1 w-full rounded-full bg-gray-200">
          <div
            className={cn("h-1 rounded-full", active ? "bg-white/60" : "bg-[--color-primary]")}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </button>
  );
}

// ── Finding row ────────────────────────────────────────────────────────────────

const SCORE_STYLES: Record<FindingScore, string> = {
  pass:    "bg-green-50 text-green-800 border-green-200",
  review:  "bg-amber-50 text-amber-800 border-amber-200",
  flag:    "bg-red-50 text-red-800 border-red-200",
  pending: "bg-gray-50 text-gray-500 border-gray-200",
};

function FindingRow({
  finding,
  onScoreChange,
}: {
  finding: Finding
  onScoreChange?: (findingNumber: number, score: FindingScore) => Promise<void>
}) {
  const [open, setOpen] = useState(false);
  const [localScore, setLocalScore] = useState<FindingScore>(finding.score);
  const [saving, setSaving] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const hasEvidence = (finding.evidence?.length ?? 0) > 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        className="flex w-full items-start gap-3 p-4 text-left"
        onClick={() => setOpen(v => !v)}
      >
        <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
          {finding.number}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{finding.title}</span>
            {finding.is_essentials && (
              <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700 border border-indigo-200">
                E
              </span>
            )}
          </div>
          {finding.score_note && !open && (
            <p className="mt-0.5 text-sm text-gray-500 truncate">{finding.score_note}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          {/* An unscored finding shows WHY it is unscored, matching the report.
              Once the CPA picks a score the score itself takes over. */}
          {localScore === "pending" && finding.state && finding.state !== "assessed"
            ? <StateBadge state={finding.state} />
            : <ScoreBadge score={localScore} />}
        </div>
      </button>
      {open && finding.body && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 pb-4 pt-3">
          <p className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">{finding.body}</p>
          {localScore === "pending" && finding.state === "data_missing" && (
            <p className="mt-2 text-sm font-medium text-amber-700">
              Not yet assessed — requires{" "}
              {finding.required_inputs || "outstanding information requests"}.
            </p>
          )}
          {isOutOfScope(finding) && (
            <p className="mt-2 text-sm text-gray-500">
              Outside the engagement's agreed scope — not assessed. See Scope Limitations in the report.
            </p>
          )}
          {finding.score_note && (
            <p className="mt-2 text-sm font-medium text-gray-500">Note: {finding.score_note}</p>
          )}
          {hasEvidence && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEvidenceOpen(true);
              }}
              className="mt-2 text-sm font-medium text-[--color-primary] underline underline-offset-2"
            >
              View Evidence Trail ({finding.evidence!.length})
            </button>
          )}
        </div>
      )}
      {hasEvidence && (
        <FindingEvidenceDrawer
          open={evidenceOpen}
          onOpenChange={setEvidenceOpen}
          findingNumber={finding.number}
          findingTitle={finding.title}
          evidence={finding.evidence!}
        />
      )}
      {open && onScoreChange && (
        <div
          data-testid="score-selector"
          className="border-t border-gray-100 px-4 py-3 flex items-center gap-2"
        >
          {(["pass", "review", "flag", "pending"] as FindingScore[]).map(s => (
            <button
              key={s}
              disabled={saving}
              aria-label={`Score ${s}`}
              onClick={async () => {
                const prev = localScore;
                setLocalScore(s);
                setSaving(true);
                try {
                  await onScoreChange(finding.number, s);
                } catch {
                  setLocalScore(prev);
                } finally {
                  setSaving(false);
                }
              }}
              className={cn(
                "rounded border px-2 py-1 text-xs font-semibold transition-opacity",
                SCORE_STYLES[s],
                localScore === s ? "ring-2 ring-offset-1 ring-current" : "opacity-60 hover:opacity-100",
              )}
            >
              {s.toUpperCase()}
            </button>
          ))}
          {saving && (
            <span className="text-xs text-gray-400">Saving…</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface FindingsTableProps {
  findings: Finding[];
  tier?: EngagementTier;
  onScoreChange?: (findingNumber: number, score: FindingScore) => Promise<void>;
  onUpgradeTier?: () => void;
  onCpaOverride?: () => void;
}

export function FindingsTable({
  findings,
  tier = "comprehensive",
  onScoreChange,
  onUpgradeTier,
  onCpaOverride,
}: FindingsTableProps) {
  const [activeSection, setActiveSection] = useState<string>("A");

  const displayFindings =
    tier === "essentials" ? findings.filter(f => f.is_essentials) : findings;

  const sectionFindings = displayFindings.filter(f => f.section === activeSection);
  const sectionInScope    = sectionFindings.filter(f => !isOutOfScope(f));
  const sectionOutOfScope = sectionFindings.filter(f => isOutOfScope(f));

  // The grade and the counts describe what was assessed. Findings nobody
  // bought are excluded rather than counted as PENDING work outstanding.
  const scopedFindings = displayFindings.filter(f => !isOutOfScope(f));
  const outOfScopeCount = displayFindings.length - scopedFindings.length;
  const passCount    = scopedFindings.filter(f => f.score === "pass").length;
  const reviewCount  = scopedFindings.filter(f => f.score === "review").length;
  const flagCount    = scopedFindings.filter(f => f.score === "flag").length;
  const pendingCount = scopedFindings.filter(f => f.score === "pending").length;
  const grade        = computeQoEGrade(scopedFindings);

  return (
    <div className="flex flex-col gap-4 lg:h-full lg:flex-row lg:gap-6">
      {/* Left rail */}
      <aside className="space-y-1 lg:w-64 lg:flex-shrink-0">
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <GradeBadge grade={grade} />
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">QoE Grade</p>
            <p className="text-sm font-semibold text-gray-800">
              {scopedFindings.length} findings · {passCount} PASS · {reviewCount} REVIEW · {flagCount} FLAG · {pendingCount} PENDING
            </p>
            {outOfScopeCount > 0 && (
              <p className="text-xs text-gray-500">
                {outOfScopeCount} of {displayFindings.length} not in scope for this engagement
              </p>
            )}
          </div>
        </div>

        {tier === "essentials" && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-800 mb-3">
            <p className="font-semibold mb-1">
              Essentials tier — {TIER_FINDING_COUNTS.lite} findings
            </p>
            <p className="mb-2">
              Upgrade to Comprehensive for all {CATALOGUE_SIZE} findings across{" "}
              {SECTION_COUNT} sections.
            </p>
            {onUpgradeTier && (
              <button
                className="text-indigo-700 font-semibold underline underline-offset-2"
                onClick={onUpgradeTier}
              >
                Upgrade to Comprehensive →
              </button>
            )}
            {onCpaOverride && (
              <button
                className="block mt-1 text-gray-600 underline underline-offset-2"
                onClick={onCpaOverride}
              >
                CPA override
              </button>
            )}
          </div>
        )}

        {/* Section nav: dropdown below lg (avoids horizontal overflow), full rail at lg+ */}
        <div className="lg:hidden">
          <label htmlFor="section-select" className="sr-only">Jump to section</label>
          <select
            id="section-select"
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm"
          >
            {SECTIONS.map(s => (
              <option key={s.key} value={s.key}>
                {s.key} — {s.title}
              </option>
            ))}
          </select>
        </div>

        <div className="hidden space-y-1 lg:block">
          {SECTIONS.map(s => (
            <SectionRailItem
              key={s.key}
              section={s}
              findings={displayFindings}
              active={activeSection === s.key}
              onClick={() => setActiveSection(s.key)}
            />
          ))}
        </div>
      </aside>

      {/* Finding list */}
      <main className="flex-1 min-w-0 space-y-3">
        <div className="mb-2">
          <h2 className="text-base font-semibold text-gray-900">
            Section {activeSection} —{" "}
            {SECTIONS.find(s => s.key === activeSection)?.title ?? ""}
          </h2>
          <p className="text-sm text-gray-500">
            {sectionInScope.length} finding{sectionInScope.length !== 1 ? "s" : ""} in scope
            {sectionOutOfScope.length > 0 && ` · ${sectionOutOfScope.length} not in scope`}
          </p>
        </div>

        {sectionInScope.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
            <p className="text-sm text-gray-500">
              No findings in scope for this section under the{" "}
              {tier === "essentials" ? "Essentials" : "Comprehensive"} tier.
            </p>
          </div>
        ) : (
          sectionInScope.map(f => (
            <FindingRow key={f.number} finding={f} onScoreChange={onScoreChange} />
          ))
        )}

        {/* Out-of-scope findings stay reachable — the CPA can still score one
            after a scope upgrade — but below the line, so they never read as
            outstanding work. Mirrors Section 9 of the report. */}
        {sectionOutOfScope.length > 0 && (
          <div className="space-y-3 pt-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Not in scope for this engagement ({sectionOutOfScope.length})
            </p>
            {sectionOutOfScope.map(f => (
              <div key={f.number} className="opacity-60">
                <FindingRow finding={f} onScoreChange={onScoreChange} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default FindingsTable;
